import {defaults} from 'lodash';

import * as React from 'react';
import {subtract, magnitude} from '@packages/utilities/PointUtils';
import {chainFunctions} from '@packages/utilities/component';
import type {Point} from '@packages/utilities/PointUtils';
import {flushSync} from 'react-dom';

const isLeftButton: (arg1: React.MouseEvent<Element>) => boolean = (event) =>
  event && event.button === 0;

const getAbsoluteCoordinates = ({
  clientX,
  clientY,
}: React.MouseEvent<any> | MouseEvent): Point => ({
  x: clientX,
  y: clientY,
});

const getBoxCoordinates = ({
  top,
  left,
}: {
  top: number;
  left: number;
}): Point => ({
  x: left,
  y: top,
});

const getCoordinatesFromState = {
  absolute: ({lastCoordinates}: State): Point | null => lastCoordinates,

  relative: ({lastCoordinates, boxCoordinates}: State): Point | null =>
    lastCoordinates &&
    boxCoordinates &&
    subtract(lastCoordinates, boxCoordinates),

  delta: ({initialCoordinates, lastCoordinates}: State): Point | null =>
    lastCoordinates &&
    initialCoordinates &&
    subtract(lastCoordinates, initialCoordinates),
} as const;

const modes = {
  ABSOLUTE: 'absolute',
  DELTA: 'delta',
  RELATIVE: 'relative',
} as const;

type State = {
  initialCoordinates: null | Point;
  lastCoordinates: null | Point;
  boxCoordinates: null | Point;
};

const initialState: State = {
  initialCoordinates: null,
  lastCoordinates: null,
  boxCoordinates: null,
};

type DraggableProps = {
  /** The mouse should move at least this many pixels to register as a drag */
  threshold?: number;
  /** Or the mouse should be held down for at least these many milliseconds */
  delay?: number;
  /** What values to call handlers with; one of 'absolute', 'relative', 'delta' */
  mode: 'absolute' | 'delta' | 'relative';
  /** Must be true to fire onDrag events, meaning the draggable component needs
   * to have an ancestor managing the `dragging` prop
   */
  dragging?: boolean;
  preventDefault?: boolean;
  onDragStart?: (arg1: Point) => any;
  onDrag?: (arg1: Point) => any;
  onDragEnd?: () => any;
  onMouseDown?: (arg1: React.MouseEvent<Element>) => void;
};

export default <BaseProps = any>(
  BaseComponent: React.ComponentType<BaseProps>
) => {
  const factory = (factoryProps: any) =>
    React.createElement(
      BaseComponent as React.ComponentType<any>,
      factoryProps
    );

  class Draggable extends React.Component<
    DraggableProps & {forwardedRef?: React.Ref<any>},
    State
  > {
    static modes = modes;

    state = initialState;
    timerId = null;
    // What do we need this for: https://www.loom.com/share/08f7aa75c72d46a8ba14a553d8ce3440?sid=672ca0a3-f49b-4925-981a-aa81e3c9bb13
    started = false;

    doc: Document | null = null;
    addedListeners = false;

    startDrag = (state?: State | null) => {
      if (this.started) return;
      state = state || this.state;
      const {onDragStart} = this.props;
      if (onDragStart) {
        const coords = getCoordinatesFromState[this.props.mode](state);
        if (coords) onDragStart(coords);
      }
      this.started = true;
    };

    drag = (state?: State | null) => {
      state = state || this.state;
      const {onDrag} = this.props;
      if (onDrag) {
        const coords = getCoordinatesFromState[this.props.mode](state);
        if (coords) onDrag(coords);
      }
    };

    start = (event: React.MouseEvent<Element>) => {
      if (!isLeftButton(event)) {
        return;
      }

      const {delay = 0, threshold = 0, preventDefault} = this.props;
      const coordinates = getAbsoluteCoordinates(event);
      const statePatch = {
        initialCoordinates: coordinates,
        lastCoordinates: coordinates,
        boxCoordinates:
          this.props.mode === modes.RELATIVE
            ? getBoxCoordinates(event.currentTarget.getBoundingClientRect())
            : null,
      } as const;

      const doc = event.currentTarget.ownerDocument;
      if (this.doc !== doc) {
        this.removeListeners();
        this.doc = doc;
      }

      this.addListeners();

      if (delay) {
        this.setState(statePatch);
        // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'null'.
        this.timerId = setTimeout(this.startDrag, delay);
      } else {
        preventDefault && event.preventDefault();
        event.stopPropagation();
        this.setState(
          statePatch,
          threshold == null || threshold <= 0 ? this.startDrag : undefined
        );
      }
    };

    move = (event: MouseEvent) => {
      const {dragging, threshold = 0} = this.props;
      const mouseIsDown = this.state.initialCoordinates;

      if ((threshold == null || threshold <= 0) && !dragging && !mouseIsDown) {
        return;
      }
      const {initialCoordinates} = this.state;
      const coordinates = getAbsoluteCoordinates(event);

      const statePatch = {
        lastCoordinates: coordinates,
        // Set initial coords in case dragging initiated from outside
        initialCoordinates: initialCoordinates
          ? initialCoordinates
          : coordinates,
      } as const;

      if (dragging) {
        event.preventDefault();
        event.stopPropagation();
        this.setState(statePatch, this.drag);
      } else if (
        mouseIsDown &&
        threshold &&
        magnitude(
          subtract(statePatch.lastCoordinates, statePatch.initialCoordinates)
        ) > threshold
      ) {
        this.timerId && clearTimeout(this.timerId);
        flushSync(() => {
          this.setState(statePatch, this.startDrag);
        });
      } else {
        this.setState(statePatch);
      }
    };

    end = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (this.props.onDragEnd) {
        this.props.onDragEnd();
      }

      this.timerId && clearTimeout(this.timerId);
      this.removeListeners();
      this.started = false;
    };

    addListeners() {
      const doc = this.doc;
      if (doc) {
        doc.addEventListener('mousemove', this.move);
        doc.addEventListener('mouseup', this.end);
        this.addedListeners = true;
      }
    }

    removeListeners() {
      if (this.addedListeners) {
        const doc = this.doc;
        if (doc) {
          doc.removeEventListener('mousemove', this.move);
          doc.removeEventListener('mouseup', this.end);
        }
        this.addedListeners = false;
      }
    }

    componentWillUnmount() {
      this.timerId && clearTimeout(this.timerId);
      this.removeListeners();
      this.doc = null;
      this.started = false;
    }

    render() {
      const {forwardedRef, ...rest} = this.props;
      return factory(
        defaults(
          {
            onMouseDown:
              typeof rest.onMouseDown === 'function'
                ? chainFunctions(this.start, rest.onMouseDown)
                : this.start,
            ref: forwardedRef,
          },
          rest
        )
      );
    }
  }

  // `Omit<BaseProps, keyof DraggableProps>` — when a BaseComponent's prop
  // signature overlaps with DraggableProps (e.g. an HTML `<div>`'s
  // `onDrag: DragEventHandler` collides with `onDrag?: (arg1: Point) => any`
  // here), the wrapper takes precedence so callers see the drag-handler shape
  // this HOC actually delivers.
  const ForwardedDraggable = React.forwardRef<
    any,
    Omit<BaseProps, keyof DraggableProps> & DraggableProps
  >((props, ref) =>
    React.createElement(Draggable, {...props, forwardedRef: ref})
  );

  return Object.assign(ForwardedDraggable, {modes});
};
